<template>
    <section class="px-4 pt-0 pb-5 mx-auto max-w-5xl lg:pt-0 lg:pb-10 xl:px-0">
        <div class="text-2xl text-center sm:text-3xl md:text-32 font-ww-bold" v-html="mainHeading">
        </div>
        <div class="mt-6 text-base md:text-lg font-ww-regular" v-html="introText">
        </div>
        <div>
            <div class="mt-8 text-xl text-center md:text-2xl font-ww-bold" v-html="optionsHeading">
            </div>
            <div class="mx-auto mt-8 space-y-5 max-w-4xl">
                <div v-for="(option, index) in options" :key="index" class="flex gap-x-4 items-start p-6 rounded-2xl"
                    :class="option.isRecommended ? 'bg-[#F1FBC8] border-[#C4E538] border-2' : 'bg-lifeboost-blue-gray'">
                    <div class="basis-5">
                        <svg class="mt-1" width="20" height="20" viewBox="0 0 20 20" fill="none"
                            xmlns="http://www.w3.org/2000/svg">
                            <circle cx="10" cy="10" r="9.5" fill="white" stroke="#DCDCDC" />
                            <circle v-if="option.isRecommended" cx="10" cy="10" r="6" fill="#C4E538" />
                        </svg>
                    </div>
                    <div>
                        <div class="text-sm sm:text-base lg:text-lg font-ww-bold" v-html="option.title">
                        </div>
                        <div class="text-sm sm:text-base lg:text-lg font-ww-regular !leading-5"
                            v-html="option.description">
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div class="bg-[#FBF0E5] border-2 border-[#E48414] md:py-12 py-8 md:px-12 px-8 rounded-2xl md:mt-10 mt-5">
            <div class="text-base text-center lg:text-17 font-ww-regular !leading-5 lg:px-20" v-html="ctaText">
            </div>
            <div class="text-base text-center lg:text-17 font-ww-bold !leading-5 md:mt-8 mt-4" v-html="ctaHeading">
            </div>
            <div class="mt-4 text-center md:mt-10">
                <button @click="scrollTo(buttonHref, 'bottom')"
                    class="inline-flex justify-center items-center px-8 py-4 text-lg font-bold text-white rounded-full transition-colors duration-200 lg:w-7/12 font-ww-medium btn-red hover:bg-red-600">
                    {{ buttonText }}
                    <svg class="ml-2 w-5 h-5" width="22" height="15" viewBox="0 0 22 15" fill="none"
                        xmlns="http://www.w3.org/2000/svg">
                        <path
                            d="M13.3007 0.50283C13.2069 0.595793 13.1325 0.706394 13.0818 0.828253C13.031 0.950112 13.0049 1.08082 13.0049 1.21283C13.0049 1.34484 13.031 1.47555 13.0818 1.59741C13.1325 1.71927 13.2069 1.82987 13.3007 1.92283L17.9007 6.52283L1.5 6.33529C1.23478 6.33529 0.98043 6.44065 0.792893 6.62819C0.605357 6.81572 0.5 7.07008 0.5 7.33529C0.5 7.60051 0.605357 7.85486 0.792893 8.0424C0.98043 8.22994 1.23478 8.33529 1.5 8.33529L17.8807 8.52283L13.3007 13.0928C13.1144 13.2802 13.0099 13.5336 13.0099 13.7978C13.0099 14.062 13.1144 14.3155 13.3007 14.5028C13.488 14.6891 13.7415 14.7936 14.0057 14.7936C14.2699 14.7936 14.5233 14.6891 14.7107 14.5028L21.0707 8.14283C21.1563 8.0607 21.2244 7.96211 21.2709 7.85299C21.3174 7.74386 21.3414 7.62646 21.3414 7.50783C21.3414 7.3892 21.3174 7.2718 21.2709 7.16267C21.2244 7.05355 21.1563 6.95496 21.0707 6.87283L14.7207 0.50283C14.6277 0.409101 14.5171 0.334707 14.3952 0.283938C14.2734 0.23317 14.1427 0.207031 14.0107 0.207031C13.8787 0.207031 13.748 0.23317 13.6261 0.283938C13.5042 0.334707 13.3936 0.409101 13.3007 0.50283Z"
                            fill="white" />
                    </svg>
                </button>
            </div>
        </div>
    </section>
</template>

<script setup>
defineProps({
    mainHeading: {
        type: String,
        default: 'It\'s Your Choice, Your Life.'
    },
    introText: {
        type: String,
        default: 'We know you\'ll love Mindflow. More energy, more focus, more productivity.<br><br>But, the decision is yours.<br><br>You\'re 100% covered by our 90-day "Hide-It-from-Your-Grandma" Guarantee (see above). So you have nothing to lose.'
    },
    optionsHeading: {
        type: String,
        default: 'You have three options.'
    },
    options: {
        type: Array,
        default: () => [
            {
                title: 'OPTION 1',
                description: '<span class="font-ww-bold">Do nothing.</span> Keep drinking your regular coffee or energy drinks. Expect jitters and afternoon slumps. Miss half of every day due to tiredness.',
                isRecommended: false
            },
            {
                title: 'OPTION 2',
                description: '<span class="font-ww-bold">Piece all the ingredients together on your own.</span> You can find all the ingredients in Mindflow online. However, they\'re likely not the same level of quality or dosage. And, you\'ll spend hundreds of dollars to buy them separately.',
                isRecommended: false
            },
            {
                title: 'OPTION 3',
                description: '<span class="font-ww-bold">Make it easy on yourself.</span> One scoop of Mindflow, one cup of hot or cold water, mix for 10 seconds, and upgrade your day. Every day.',
                isRecommended: true
            }
        ]
    },
    ctaText: {
        type: String,
        default: 'Hurry, we only have a limited number of bags of Mindflow available because the powerful, patented ingredients we use are in limited supply (and are hard to get).'
    },
    ctaHeading: {
        type: String,
        default: 'Click the button below now to boost your brain, increase your focus, and be the best version of yourself now.'
    },
    buttonText: {
        type: String,
        default: 'ORDER NOW'
    },
    buttonHref: {
        type: String,
        default: 'products'
    }
})

defineBuilderComponent({
    friendlyName: 'Decision Options with CTA',
    canHaveChildren: false,
    inputs: [
        {
            name: 'mainHeading',
            type: 'richText',
            defaultValue: 'It\'s Your Choice, Your Life.'
        },
        {
            name: 'introText',
            type: 'richText',
            defaultValue: 'We know you\'ll love Mindflow. More energy, more focus, more productivity.<br><br>But, the decision is yours.<br><br>You\'re 100% covered by our 90-day "Hide-It-from-Your-Grandma" Guarantee (see above). So you have nothing to lose.'
        },
        {
            name: 'optionsHeading',
            type: 'richText',
            defaultValue: 'You have three options.'
        },
        {
            name: 'options',
            type: 'list',
            subFields: [
                {
                    name: 'title',
                    type: 'richText'
                },
                {
                    name: 'description',
                    type: 'richText'
                },
                {
                    name: 'isRecommended',
                    type: 'boolean'
                }
            ],
            defaultValue: [
                {
                    title: 'OPTION 1',
                    description: '<span class="font-ww-bold">Do nothing.</span> Keep drinking your regular coffee or energy drinks. Expect jitters and afternoon slumps. Miss half of every day due to tiredness.',
                    isRecommended: false
                },
                {
                    title: 'OPTION 2',
                    description: '<span class="font-ww-bold">Piece all the ingredients together on your own.</span> You can find all the ingredients in Mindflow online. However, they\'re likely not the same level of quality or dosage. And, you\'ll spend hundreds of dollars to buy them separately.',
                    isRecommended: false
                },
                {
                    title: 'OPTION 3',
                    description: '<span class="font-ww-bold">Make it easy on yourself.</span> One scoop of Mindflow, one cup of hot or cold water, mix for 10 seconds, and upgrade your day. Every day.',
                    isRecommended: true
                }
            ]
        },
        {
            name: 'ctaText',
            type: 'richText',
            defaultValue: 'Hurry, we only have a limited number of bags of Mindflow available because the powerful, patented ingredients we use are in limited supply (and are hard to get).'
        },
        {
            name: 'ctaHeading',
            type: 'richText',
            defaultValue: 'Click the button below now to boost your brain, increase your focus, and be the best version of yourself now.'
        },
        {
            name: 'buttonText',
            type: 'text',
            defaultValue: 'ORDER NOW'
        },
        {
            name: 'buttonHref',
            type: 'text',
            defaultValue: 'products'
        }
    ]
})
</script>